/**
 * Stellenauftraege (job_postings) — Kern der Vermittlungskette.
 * Ein Stellenauftrag gehoert immer zu einem Kunden und traegt die Vorschlaege.
 * Spec: docs/superpowers/specs/2026-08-20-prozesskette-design.md
 */

import { SubmissionStatus } from './submissions.service';

const API_BASE = '/api/v1/job-postings';

export type JobOrderStatus = 'offen' | 'in_arbeit' | 'angehalten' | 'besetzt' | 'storniert';

export const JOB_ORDER_STATUS_CONFIG: Record<JobOrderStatus, { label: string; color: string; bgColor: string }> = {
  offen: { label: 'Offen', color: '#3B82F6', bgColor: 'rgba(59, 130, 246, 0.12)' },
  in_arbeit: { label: 'In Arbeit', color: '#8B5CF6', bgColor: 'rgba(139, 92, 246, 0.12)' },
  angehalten: { label: 'Angehalten', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.12)' },
  besetzt: { label: 'Besetzt', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.12)' },
  storniert: { label: 'Storniert', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.12)' },
};

export interface JobOrder {
  id: string;
  company_id: string;
  title: string;
  description?: string | null;
  requirements?: string | null;
  location?: string | null;
  department?: string | null;
  employment_type?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  status: JobOrderStatus;
  priority?: string | null;
  openings_count: number;
  filled_count: number;
  deadline?: string | null;
  client_company_id: string | null;
  client_contact_id: string | null;
  owner_user_id: string | null;
  fee_percent: number | null;
  created_at: string;
  updated_at: string;
}

export interface PipelineEntry {
  id: string;
  status: SubmissionStatus;
  statusChangedAt: string | null;
  createdAt: string;
  submittedAt: string | null;
  assignedTo: string | null;
  rejectedReason: string | null;
  clientFeedback: string | null;
  candidateId: string | null;
  candidateName: string;
  candidatePosition: string | null;
  candidateStage: string | null;
  allowedTargets: SubmissionStatus[];
}

export interface JobOrderPipeline {
  jobOrder: {
    id: string;
    title: string;
    status: JobOrderStatus;
    openingsCount: number;
    filledCount: number;
    clientCompanyId: string | null;
    clientContactId: string | null;
    ownerUserId: string | null;
    feePercent: number | null;
  };
  total: number;
  groups: Record<SubmissionStatus, PipelineEntry[]>;
}

export interface ListJobOrdersParams {
  status?: JobOrderStatus;
  client_company_id?: string;
  owner_user_id?: string;
  department?: string;
}

// Diese Routen antworten ohne success-Huelle (Bestandsverhalten).
async function fetchPlain<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json?.error || 'Anfrage fehlgeschlagen');
  }
  return json as T;
}

const jobOrdersService = {
  async list(params: ListJobOrdersParams = {}): Promise<JobOrder[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.client_company_id) qs.set('client_company_id', params.client_company_id);
    if (params.owner_user_id) qs.set('owner_user_id', params.owner_user_id);
    if (params.department) qs.set('department', params.department);
    const s = qs.toString();
    return fetchPlain<JobOrder[]>(`${API_BASE}${s ? `?${s}` : ''}`);
  },

  async get(id: string): Promise<JobOrder> {
    return fetchPlain<JobOrder>(`${API_BASE}/${id}`);
  },

  async pipeline(id: string): Promise<JobOrderPipeline> {
    return fetchPlain<JobOrderPipeline>(`${API_BASE}/${id}/pipeline`);
  },

  async create(payload: Partial<JobOrder>): Promise<JobOrder> {
    return fetchPlain<JobOrder>(API_BASE, { method: 'POST', body: JSON.stringify(payload) });
  },

  async update(id: string, updates: Partial<JobOrder>): Promise<JobOrder> {
    return fetchPlain<JobOrder>(`${API_BASE}/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
  },
};

export default jobOrdersService;
